/**
 * B2B Credit Application Form Handler
 */

(function() {
  'use strict';

  const config = window.__creditAppConfig || {};
  const form = document.getElementById('CreditApplicationForm');
  const progressSteps = document.querySelectorAll('.progress-step');
  let currentStep = 1;
  const totalSteps = 5;

  // Signature pad instances
  const signaturePads = {};

  /**
   * Initialize the form
   */
  function init() {
    if (!form) return;

    // Initialize signature pads after a short delay to ensure DOM is ready
    setTimeout(initSignaturePads, 100);

    // Setup event listeners
    setupNavigationButtons();
    setupConditionalFields();
    setupFileUploads();
    setupFormValidation();
    setupFormSubmission();

    // Set default dates
    setDefaultDates();

    // Show first step
    showStep(1);
  }

  /**
   * Set default dates to today
   */
  function setDefaultDates() {
    const today = new Date().toISOString().split('T')[0];
    const dateFields = ['agreement_date', 'guarantee_date'];
    dateFields.forEach(id => {
      const field = document.getElementById(id);
      if (field && !field.value) {
        field.value = today;
      }
    });
  }

  /**
   * Initialize all signature pads
   * Note: Signature pads are now initialized via inline script in the form HTML
   * for better cross-device compatibility. This function is kept for compatibility.
   */
  function initSignaturePads() {
    // Signatures are initialized inline in the HTML for reliability
    // See credit-application-form.liquid for the inline initialization
  }

  /**
   * Simple Signature Pad Class
   * DEPRECATED: Signature pads are now initialized inline in the HTML.
   * This class is kept for reference but not used.
   */
  function SimpleSignaturePad(canvas, hiddenInputId) {
    // Not used - signature pads are now initialized inline
    return null;
  }

  /**
   * Setup navigation buttons
   */
  function setupNavigationButtons() {
    document.querySelectorAll('.btn-next').forEach(btn => {
      btn.addEventListener('click', function() {
        const nextStep = parseInt(this.dataset.next);
        if (validateStep(currentStep)) {
          showStep(nextStep);
        }
      });
    });

    document.querySelectorAll('.btn-prev').forEach(btn => {
      btn.addEventListener('click', function() {
        const prevStep = parseInt(this.dataset.prev);
        showStep(prevStep);
      });
    });
  }

  /**
   * Show a specific step
   */
  function showStep(step) {
    // Hide all sections
    document.querySelectorAll('.credit-app__section').forEach(section => {
      section.classList.remove('active');
    });

    // Show target section
    const targetSection = document.querySelector(`.credit-app__section[data-step="${step}"]`);
    if (targetSection) {
      targetSection.classList.add('active');
    }

    // Update progress
    progressSteps.forEach(progressStep => {
      const stepNum = parseInt(progressStep.dataset.step);
      progressStep.classList.remove('active', 'completed');

      if (stepNum < step) {
        progressStep.classList.add('completed');
      } else if (stepNum === step) {
        progressStep.classList.add('active');
      }
    });

    currentStep = step;

    // Scroll to top of form
    form.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /**
   * Validate a step
   */
  function validateStep(step) {
    const section = document.querySelector(`.credit-app__section[data-step="${step}"]`);
    if (!section) return true;

    let isValid = true;

    // Clear previous errors
    section.querySelectorAll('.form-error').forEach(el => el.remove());
    section.querySelectorAll('.has-error').forEach(el => el.classList.remove('has-error'));

    // Get all required fields that are visible
    const requiredFields = section.querySelectorAll('[required]');

    requiredFields.forEach(field => {
      // Skip hidden fields and fields in hidden containers
      if (field.type === 'hidden') return;
      const parent = field.closest('[style*="display: none"]');
      if (parent) return;

      if (!field.value || !field.value.trim()) {
        isValid = false;
        showFieldError(field, 'This field is required');
      } else if (field.type === 'email' && !isValidEmail(field.value)) {
        isValid = false;
        showFieldError(field, 'Please enter a valid email address');
      } else if (field.type === 'tel' && !isValidPhone(field.value)) {
        isValid = false;
        showFieldError(field, 'Please enter a valid phone number');
      }
    });

    // Special validation for step 1 (business type)
    if (step === 1) {
      const businessTypeChecked = document.querySelectorAll('input[name="business_type[]"]:checked').length > 0;
      if (!businessTypeChecked) {
        isValid = false;
        const businessTypeGroup = document.querySelector('.checkbox-group');
        if (businessTypeGroup) {
          showFieldError(businessTypeGroup.closest('.form-group'), 'Please select at least one business type');
        }
      }
    }

    // Special validation for step 5 (signatures required)
    if (step === 5) {
      // Agreement checkbox
      const agreementCheckbox = document.getElementById('agreement_accepted');
      if (agreementCheckbox && !agreementCheckbox.checked) {
        isValid = false;
        showFieldError(agreementCheckbox.closest('.form-group--checkbox'), 'You must accept the terms and conditions');
      }

      // Agreement signature (use global pad from inline script)
      if (window.agreementSigPad && window.agreementSigPad.isEmpty()) {
        isValid = false;
        window.agreementSigPad.showError();
      }

      // Guarantee signature
      if (window.guaranteeSigPad && window.guaranteeSigPad.isEmpty()) {
        isValid = false;
        window.guaranteeSigPad.showError();
      }
    }

    return isValid;
  }

  /**
   * Show field error
   */
  function showFieldError(field, message) {
    const formGroup = field.closest('.form-group') || field.closest('.form-group--checkbox') || field.closest('.form-group--full');
    if (formGroup && !formGroup.querySelector('.form-error')) {
      formGroup.classList.add('has-error');
      const error = document.createElement('div');
      error.className = 'form-error';
      error.textContent = message;
      formGroup.appendChild(error);
    }
  }

  /**
   * Validate email
   */
  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  /**
   * Validate phone
   */
  function isValidPhone(phone) {
    return /^[\d\s\-\(\)\+\.]{7,20}$/.test(phone);
  }

  /**
   * Setup conditional fields
   */
  function setupConditionalFields() {
    // Billing address same as business
    const billingSameCheckbox = document.getElementById('billing_same_as_business');
    const billingFields = document.getElementById('billing_address_fields');

    if (billingSameCheckbox && billingFields) {
      billingSameCheckbox.addEventListener('change', function() {
        billingFields.style.display = this.checked ? 'none' : 'block';
      });
    }

    // Shipping address same as business
    const shippingSameCheckbox = document.getElementById('shipping_same_as_business');
    const shippingFields = document.getElementById('shipping_address_fields');

    if (shippingSameCheckbox && shippingFields) {
      shippingSameCheckbox.addEventListener('change', function() {
        shippingFields.style.display = this.checked ? 'none' : 'block';
      });
    }

    // Business type "Other" field
    const businessTypeOtherCheckbox = document.getElementById('business_type_other_checkbox');
    const businessTypeOtherWrap = document.getElementById('business_type_other_wrap');

    if (businessTypeOtherCheckbox && businessTypeOtherWrap) {
      businessTypeOtherCheckbox.addEventListener('change', function() {
        businessTypeOtherWrap.style.display = this.checked ? 'block' : 'none';
        const otherInput = document.getElementById('business_type_other_specify');
        if (otherInput) {
          otherInput.required = this.checked;
        }
      });
    }

    // AP Contact same as main
    const apSameCheckbox = document.getElementById('ap_contact_same_as_main');
    const apFields = document.getElementById('ap_contact_fields');

    if (apSameCheckbox && apFields) {
      apSameCheckbox.addEventListener('change', function() {
        apFields.style.display = this.checked ? 'none' : 'block';
      });
    }

    // Owner same as main contact
    const ownerSameCheckbox = document.getElementById('owner_same_as_main');

    if (ownerSameCheckbox) {
      ownerSameCheckbox.addEventListener('change', function() {
        if (this.checked) {
          const mainName = document.getElementById('main_contact_name');
          const mainTitle = document.getElementById('main_contact_title');
          const mainPhone = document.getElementById('main_contact_phone');
          const mainEmail = document.getElementById('main_contact_email');

          const ownerName = document.getElementById('owner_name');
          const ownerTitle = document.getElementById('owner_title');
          const ownerPhone = document.getElementById('owner_phone');
          const ownerEmail = document.getElementById('owner_email');

          if (mainName && ownerName) ownerName.value = mainName.value;
          if (mainTitle && ownerTitle) ownerTitle.value = mainTitle.value;
          if (mainPhone && ownerPhone) ownerPhone.value = mainPhone.value;
          if (mainEmail && ownerEmail) ownerEmail.value = mainEmail.value;
        }
      });
    }

    // Guarantor same as Owner
    const guarantorSameCheckbox = document.getElementById('guarantor_same_as_owner');

    if (guarantorSameCheckbox) {
      guarantorSameCheckbox.addEventListener('change', function() {
        if (this.checked) {
          const ownerName = document.getElementById('owner_name');
          const ownerTitle = document.getElementById('owner_title');
          const ownerPhone = document.getElementById('owner_phone');
          const ownerEmail = document.getElementById('owner_email');

          const guarantorName = document.getElementById('guarantor_name');
          const guarantorTitle = document.getElementById('guarantor_title');
          const guarantorPhone = document.getElementById('guarantor_phone');
          const guarantorEmail = document.getElementById('guarantor_email');

          if (ownerName && guarantorName) guarantorName.value = ownerName.value;
          if (ownerTitle && guarantorTitle) guarantorTitle.value = ownerTitle.value;
          if (ownerPhone && guarantorPhone) guarantorPhone.value = ownerPhone.value;
          if (ownerEmail && guarantorEmail) guarantorEmail.value = ownerEmail.value;
        }
      });
    }
  }

  /**
   * Setup file uploads
   */
  function setupFileUploads() {
    document.querySelectorAll('.file-upload__input').forEach(input => {
      input.addEventListener('change', function() {
        const filenameEl = document.getElementById(this.id + '_filename');
        const container = this.closest('.file-upload');

        if (this.files.length > 0) {
          const file = this.files[0];

          if (file.size > 10 * 1024 * 1024) {
            console.error('File size must be less than 10MB');
            this.value = '';
            if (filenameEl) filenameEl.textContent = '';
            container.classList.remove('file-upload--has-file');
            return;
          }

          const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];
          const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

          if (!allowedExtensions.includes(fileExtension)) {
            console.error('Invalid file type');
            this.value = '';
            if (filenameEl) filenameEl.textContent = '';
            container.classList.remove('file-upload--has-file');
            return;
          }

          if (filenameEl) {
            filenameEl.textContent = file.name;
          }
          container.classList.add('file-upload--has-file');
        } else {
          if (filenameEl) filenameEl.textContent = '';
          container.classList.remove('file-upload--has-file');
        }
      });
    });
  }

  /**
   * Setup form validation
   */
  function setupFormValidation() {
    form.querySelectorAll('input, select, textarea').forEach(field => {
      field.addEventListener('blur', function() {
        const formGroup = this.closest('.form-group');
        if (formGroup) {
          formGroup.classList.remove('has-error');
          const error = formGroup.querySelector('.form-error');
          if (error) error.remove();
        }
      });
    });
  }

  /**
   * Setup form submission
   */
  function setupFormSubmission() {
    form.addEventListener('submit', async function(e) {
      e.preventDefault();

      if (!validateStep(currentStep)) {
        return;
      }

      const submitBtn = document.getElementById('submit_application');
      const btnText = submitBtn.querySelector('.btn__text');
      const btnLoading = submitBtn.querySelector('.btn__loading');

      submitBtn.disabled = true;
      if (btnText) btnText.style.display = 'none';
      if (btnLoading) btnLoading.style.display = 'inline-flex';

      const formData = new FormData(form);

      // Ensure signature data is included
      if (signaturePads.agreement && !signaturePads.agreement.isEmpty()) {
        formData.set('agreement_signature_data', signaturePads.agreement.getData());
      }
      if (signaturePads.guarantee && !signaturePads.guarantee.isEmpty()) {
        formData.set('guarantee_signature_data', signaturePads.guarantee.getData());
      }

      // Handle same-as checkboxes
      const billingSame = document.getElementById('billing_same_as_business');
      if (billingSame && billingSame.checked) {
        formData.set('billing_address[street_1]', formData.get('business_address[street_1]'));
        formData.set('billing_address[street_2]', formData.get('business_address[street_2]'));
        formData.set('billing_address[city]', formData.get('business_address[city]'));
        formData.set('billing_address[state]', formData.get('business_address[state]'));
        formData.set('billing_address[zip]', formData.get('business_address[zip]'));
        formData.set('billing_address[country]', formData.get('business_address[country]'));
      }

      const shippingSame = document.getElementById('shipping_same_as_business');
      if (shippingSame && shippingSame.checked) {
        formData.set('shipping_address[street_1]', formData.get('business_address[street_1]'));
        formData.set('shipping_address[street_2]', formData.get('business_address[street_2]'));
        formData.set('shipping_address[city]', formData.get('business_address[city]'));
        formData.set('shipping_address[state]', formData.get('business_address[state]'));
        formData.set('shipping_address[zip]', formData.get('business_address[zip]'));
        formData.set('shipping_address[country]', formData.get('business_address[country]'));
      }

      const apSame = document.getElementById('ap_contact_same_as_main');
      if (apSame && apSame.checked) {
        formData.set('ap_contact_name', formData.get('main_contact_name'));
        formData.set('ap_contact_email', formData.get('main_contact_email'));
        formData.set('ap_contact_phone', formData.get('main_contact_phone'));
      }

      try {
        const apiUrl = config.apiUrl || '/apps/credit-app/api/applications/submit';

        const response = await fetch(apiUrl, {
          method: 'POST',
          body: formData
        });

        const result = await response.json();

        if (result.success) {
          form.style.display = 'none';
          document.getElementById('FormProgress').style.display = 'none';

          const successEl = document.getElementById('application_success');
          const refEl = document.getElementById('reference_number');

          if (refEl) refEl.textContent = result.reference_number;
          successEl.style.display = 'block';
          successEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          console.error('Validation errors:', result.errors);

          let errorHtml = '<strong>Please correct the following errors:</strong><ul>';
          if (result.errors) {
            Object.entries(result.errors).forEach(([field, message]) => {
              errorHtml += `<li><strong>${field}:</strong> ${message}</li>`;
            });
          } else if (result.error) {
            errorHtml += `<li>${result.error}</li>`;
          }
          errorHtml += '</ul>';

          form.style.display = 'none';
          document.getElementById('FormProgress').style.display = 'none';

          const errorEl = document.getElementById('application_error');
          const errorMsg = document.getElementById('error_message');

          if (errorMsg) errorMsg.innerHTML = errorHtml;
          errorEl.style.display = 'block';
          errorEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } catch (error) {
        console.error('Submission error:', error);

        form.style.display = 'none';
        document.getElementById('FormProgress').style.display = 'none';

        const errorEl = document.getElementById('application_error');
        const errorMsg = document.getElementById('error_message');

        if (errorMsg) errorMsg.textContent = error.message || 'Something went wrong. Please try again.';
        errorEl.style.display = 'block';
      } finally {
        submitBtn.disabled = false;
        if (btnText) btnText.style.display = 'inline';
        if (btnLoading) btnLoading.style.display = 'none';
      }
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
